import { useState, useEffect } from "react";
import { createPortal } from 'react-dom';
import { supabase } from "../lib/supabase";
import { db } from "../lib/db";
import { useApp } from "../context/AppContext";

const isDemo = process.env.REACT_APP_DEMO_MODE === 'true';

// ---------------------------------------------------------------------------
// Mock Data
// ---------------------------------------------------------------------------

const MOCK_PORTAL_USERS = [
  { id: "PU001", patient_id: "P003", email: "james.hartley@email.com", first_name: "James", last_name: "Hartley", date_of_birth: "1968-05-14", activated: true, activated_at: "2025-08-15T10:00:00Z", last_login: "2026-03-07T14:30:00Z", access_code: "123456" },
  { id: "PU002", patient_id: "P001", email: "maria.garcia@email.com", first_name: "Maria", last_name: "Garcia", date_of_birth: "1978-09-22", activated: true, activated_at: "2025-10-01T09:00:00Z", last_login: "2026-03-10T11:00:00Z", access_code: "654321" },
  { id: "PU003", patient_id: "P002", email: "robert.johnson@email.com", first_name: "Robert", last_name: "Johnson", date_of_birth: "1955-11-30", activated: false, activated_at: null, last_login: null, access_code: "789012" },
  { id: "PU004", patient_id: "P004", email: "sarah.chen@email.com", first_name: "Sarah", last_name: "Chen", date_of_birth: "1990-03-14", activated: false, activated_at: null, last_login: null, access_code: "345678" },
];

const INITIAL_PENDING_FORMS = [
  { id: "F001", patient_name: "James Hartley", patient_id: "P003", form_type: "post_op_instructions", status: "acknowledged", submitted_at: "2026-03-08T10:30:00Z", form_data: { acknowledged: true, acknowledged_at: "2026-03-08T10:30:00Z" } },
  { id: "F002", patient_name: "Maria Garcia", patient_id: "P001", form_type: "intake", status: "submitted", submitted_at: "2026-03-12T14:15:00Z", form_data: { first_name: "Maria", last_name: "Garcia", chief_complaint: "Wrist pain and numbness", pain_scale: 6, conditions: ["hypertension"] } },
  { id: "F003", patient_name: "Robert Johnson", patient_id: "P002", form_type: "intake", status: "pending", submitted_at: null, form_data: {} },
];

const INITIAL_PORTAL_MSGS = [
  { id: "PM001", patient_name: "James Hartley", patient_id: "P003", subject: "Question about pain medication schedule", body: "Hi, I wanted to ask about when I can start tapering off the Oxycodone...", created_at: "2026-03-07T14:00:00Z", is_read: false, is_urgent: false },
  { id: "PM002", patient_name: "Maria Garcia", patient_id: "P001", subject: "[Refill Request] Meloxicam 15mg", body: "Requesting refill of Meloxicam 15mg. Pharmacy: CVS Scottsdale (480) 555-2100", created_at: "2026-03-11T09:30:00Z", is_read: false, is_urgent: false },
  { id: "PM003", patient_name: "James Hartley", patient_id: "P003", subject: "Post-op swelling question", body: "Is it normal to still have swelling 3 days post-op?", created_at: "2026-03-08T16:00:00Z", is_read: true, is_urgent: true },
];

const INITIAL_PORTAL_BILLS = [
  { id: "PB001", patient_name: "James Hartley", patient_id: "P003", description: "Right Shoulder Arthroscopy – Patient Responsibility", dos: "2026-03-05", amount_due: 350, amount_paid: 0, status: "unpaid", due_date: "2026-04-05" },
  { id: "PB002", patient_name: "Maria Garcia", patient_id: "P001", description: "New Patient Visit Copay", dos: "2026-03-10", amount_due: 40, amount_paid: 0, status: "unpaid", due_date: "2026-04-10" },
  { id: "PB003", patient_name: "Robert Johnson", patient_id: "P002", description: "Follow-Up Visit Copay", dos: "2026-02-28", amount_due: 40, amount_paid: 40, status: "paid", due_date: "2026-03-28" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(isoStr) {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) +
    " " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function generateAccessCode() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

function formatFormType(type) {
  switch (type) {
    case "post_op_instructions": return "Post-Op Instructions";
    case "intake": return "Intake Form";
    default: return type;
  }
}

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

function Modal({ title, onClose, children, width = 480 }) {
  return createPortal(
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div style={{
        background: "var(--navy-2)",
        border: "1px solid var(--border-2)",
        borderRadius: 12,
        width: "100%",
        maxWidth: width,
        maxHeight: "90vh",
        overflowY: "auto",
        padding: 24,
        boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "var(--text-1)" }}>{title}</h3>
          <button
            onClick={onClose}
            style={{
              background: "var(--border-2)", border: "none", borderRadius: "50%",
              width: 30, height: 30, cursor: "pointer", color: "var(--text-2)",
              fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >×</button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

function FormField({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function ProviderInput({ value, onChange, placeholder, type = "text", ...rest }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: "100%", boxSizing: "border-box",
        background: "var(--navy)", border: "1px solid var(--border-2)", borderRadius: 8,
        padding: "9px 12px", color: "var(--text-1)", fontSize: 14,
        outline: "none", fontFamily: "inherit",
      }}
      {...rest}
    />
  );
}

function ProviderTextarea({ value, onChange, placeholder, rows = 4 }) {
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
      style={{
        width: "100%", boxSizing: "border-box",
        background: "var(--navy)", border: "1px solid var(--border-2)", borderRadius: 8,
        padding: "9px 12px", color: "var(--text-1)", fontSize: 14,
        outline: "none", fontFamily: "inherit", resize: "vertical",
      }}
    />
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { bg: "#1e293b", color: "#94a3b8", border: "#334155", label: "Pending" },
    submitted: { bg: "#451a03", color: "#fbbf24", border: "#78350f", label: "Submitted" },
    acknowledged: { bg: "#052e16", color: "#4ade80", border: "#14532d", label: "Acknowledged" },
    activated: { bg: "#052e16", color: "#4ade80", border: "#14532d", label: "Activated" },
    not_activated: { bg: "#1e293b", color: "#94a3b8", border: "#334155", label: "Not Activated" },
    paid: { bg: "#052e16", color: "#4ade80", border: "#14532d", label: "Paid" },
    unpaid: { bg: "#451a03", color: "#fbbf24", border: "#78350f", label: "Unpaid" },
    unread: { bg: "#1e3a5f", color: "#60a5fa", border: "#1d4ed8", label: "Unread" },
    read: { bg: "#1e293b", color: "#94a3b8", border: "#334155", label: "Read" },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 600,
    }}>
      {s.label}
    </span>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", borderBottom: "1px solid var(--border)", marginBottom: 20, gap: 0 }}>
      {tabs.map(tab => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          style={{
            padding: "10px 18px",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            background: "transparent",
            border: "none",
            borderBottom: `2px solid ${active === tab.key ? "#3b82f6" : "transparent"}`,
            color: active === tab.key ? "#3b82f6" : "var(--text-2)",
            fontFamily: "inherit",
            marginBottom: -1,
            whiteSpace: "nowrap",
            position: "relative",
            transition: "color 0.15s",
          }}
        >
          {tab.label}
          {tab.badge > 0 && (
            <span style={{
              background: "#ef4444", color: "white", borderRadius: 999,
              fontSize: 10, fontWeight: 700, padding: "1px 5px",
              marginLeft: 6, verticalAlign: "middle",
            }}>
              {tab.badge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

function TableWrapper({ children }) {
  return (
    <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid var(--border)" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        {children}
      </table>
    </div>
  );
}

function THead({ cols }) {
  return (
    <thead>
      <tr style={{ background: "var(--navy)" }}>
        {cols.map((col, i) => (
          <th key={i} style={{
            padding: "10px 14px", textAlign: "left",
            fontSize: 11, fontWeight: 700, color: "var(--text-2)",
            textTransform: "uppercase", letterSpacing: 0.6,
            borderBottom: "1px solid var(--border)", whiteSpace: "nowrap",
          }}>
            {col}
          </th>
        ))}
      </tr>
    </thead>
  );
}

function TRow({ children, highlight }) {
  return (
    <tr style={{
      borderBottom: "1px solid var(--border)",
      borderLeft: highlight ? "3px solid #ef4444" : "3px solid transparent",
      background: "transparent",
      transition: "background 0.1s",
    }}
      onMouseEnter={e => e.currentTarget.style.background = "var(--navy)"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}
    >
      {children}
    </tr>
  );
}

function TD({ children, style = {} }) {
  return (
    <td style={{ padding: "10px 14px", color: "var(--text-1)", verticalAlign: "middle", ...style }}>
      {children}
    </td>
  );
}

function ActionBtn({ onClick, children, variant = "default", disabled }) {
  const variants = {
    default: { background: "#1e293b", color: "#94a3b8", border: "1px solid #334155" },
    primary: { background: "#1d4ed8", color: "white", border: "none" },
    success: { background: "#052e16", color: "#4ade80", border: "1px solid #14532d" },
    danger: { background: "#450a0a", color: "#f87171", border: "1px solid #7f1d1d" },
    amber: { background: "#451a03", color: "#fbbf24", border: "1px solid #78350f" },
  };
  const v = variants[variant] || variants.default;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        ...v,
        borderRadius: 6, padding: "5px 10px",
        fontSize: 12, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
        fontFamily: "inherit", whiteSpace: "nowrap",
        opacity: disabled ? 0.5 : 1,
        transition: "opacity 0.15s",
      }}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Tab 1: Portal Accounts
// ---------------------------------------------------------------------------

function InviteModal({ onClose, onInvite }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [generated, setGenerated] = useState(null);

  const handleSend = () => {
    const code = generateAccessCode();
    const newUser = {
      id: "PU" + Date.now(),
      patient_id: "P" + Date.now(),
      email, first_name: firstName, last_name: lastName,
      date_of_birth: dob,
      activated: false, activated_at: null, last_login: null,
      access_code: code,
    };
    onInvite(newUser);
    setGenerated(code);
  };

  if (generated) {
    return (
      <Modal title="Invite Sent" onClose={onClose}>
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <p style={{ color: "var(--text-2)", fontSize: 14, marginBottom: 16 }}>
            Portal invitation sent to <strong style={{ color: "var(--text-1)" }}>{email}</strong>
          </p>
          <div style={{
            background: "var(--navy)", border: "1px solid var(--border-2)", borderRadius: 10,
            padding: "16px 20px", marginBottom: 16,
          }}>
            <div style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 6 }}>PATIENT ACCESS CODE</div>
            <div style={{ fontSize: 32, fontWeight: 800, color: "#4ade80", letterSpacing: 8, fontFamily: "monospace" }}>
              {generated}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-2)", marginTop: 6 }}>Share this code with the patient</div>
          </div>
          <button className="btn btn-primary" onClick={onClose} style={{ width: "100%" }}>Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Invite Patient to Portal" onClose={onClose}>
      <FormField label="First Name">
        <ProviderInput value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" />
      </FormField>
      <FormField label="Last Name">
        <ProviderInput value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" />
      </FormField>
      <FormField label="Email Address">
        <ProviderInput type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="patient@email.com" />
      </FormField>
      <FormField label="Date of Birth">
        <ProviderInput type="date" value={dob} onChange={e => setDob(e.target.value)} />
      </FormField>
      <button
        className="btn btn-primary"
        onClick={handleSend}
        disabled={!firstName || !lastName || !email || !dob}
        style={{ width: "100%", marginTop: 8 }}
      >
        Generate Code &amp; Send Invite
      </button>
    </Modal>
  );
}

function PortalAccountsTab() {
  const appCtx = useApp();
  const [users, setUsers] = useState(isDemo ? MOCK_PORTAL_USERS : []);
  const [loading, setLoading] = useState(!isDemo);
  const [showInvite, setShowInvite] = useState(false);
  const [revealedCodes, setRevealedCodes] = useState({});
  const [copiedId, setCopiedId] = useState(null);
  const [toastMsg, setToastMsg] = useState("");

  useEffect(() => {
    if (isDemo) return;
    let active = true;
    (async () => {
      try {
        // Query 1 — invites without join (patient_id is text; no FK to patients.id uuid)
        const { data: invData, error } = await db
          .from("portal_invites")
          .select("id, patient_id, email, used_at, created_at, token_expires_at")
          .order("created_at", { ascending: false })
          .limit(200);
        if (error) { console.error("[PortalAccountsTab]", error); }

        const invites = invData || [];

        // Query 2 — patient names for the collected patient_ids
        const patientIds = [...new Set(invites.map(inv => inv.patient_id).filter(Boolean))];
        let patientMap = {};
        if (patientIds.length > 0) {
          const { data: patientData } = await db
            .from("patients")
            .select("id, first_name, last_name, date_of_birth")
            .in("id", patientIds);
          patientMap = Object.fromEntries((patientData || []).map(p => [p.id, p]));
        }

        if (active) {
          const mapped = invites.map(inv => ({
            id: inv.id,
            patient_id: inv.patient_id,
            email: inv.email || "—",
            first_name: patientMap[inv.patient_id]?.first_name || "—",
            last_name: patientMap[inv.patient_id]?.last_name || "",
            date_of_birth: patientMap[inv.patient_id]?.date_of_birth || "—",
            activated: !!inv.used_at,
            activated_at: inv.used_at || null,
            last_login: null, // portal_sessions has anon-only RLS; omitted
            access_code: null,
          }));
          setUsers(mapped);
        }
      } catch (e) {
        console.error("[PortalAccountsTab]", e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 2500);
  };

  const toggleActivation = (userId) => {
    // Demo-only toggle — production uses invite/session state
    if (isDemo) setUsers(users.map(u => u.id === userId ? { ...u, activated: !u.activated } : u));
  };

  const toggleCode = (userId) => {
    if (isDemo) setRevealedCodes(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const copyInviteLink = (user) => {
    if (isDemo) {
      const link = `https://epiphanymd.com/portal/activate?code=${user.access_code}&email=${encodeURIComponent(user.email)}`;
      navigator.clipboard.writeText(link).catch(() => {});
    } else {
      // In production, the magic link is single-use and already consumed.
      // Resending opens the InviteModal pre-filled with this patient.
    }
    setCopiedId(user.id);
    showToast(isDemo ? "Invite link copied to clipboard" : "Use 'Invite Patient' to send a new magic link");
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleInvite = (newUser) => {
    if (isDemo) {
      setUsers(prev => [...prev, newUser]);
    } else {
      // Reload from Supabase after a real invite is sent
      setLoading(true);
      (async () => {
        try {
          const { data: invData } = await db
            .from("portal_invites")
            .select("id, patient_id, email, used_at, created_at, token_expires_at")
            .order("created_at", { ascending: false })
            .limit(200);
          const invites = invData || [];
          const patientIds = [...new Set(invites.map(inv => inv.patient_id).filter(Boolean))];
          let patientMap = {};
          if (patientIds.length > 0) {
            const { data: patientData } = await db
              .from("patients")
              .select("id, first_name, last_name, date_of_birth")
              .in("id", patientIds);
            patientMap = Object.fromEntries((patientData || []).map(p => [p.id, p]));
          }
          const mapped = invites.map(inv => ({
            id: inv.id,
            patient_id: inv.patient_id,
            email: inv.email || "—",
            first_name: patientMap[inv.patient_id]?.first_name || "—",
            last_name: patientMap[inv.patient_id]?.last_name || "",
            date_of_birth: patientMap[inv.patient_id]?.date_of_birth || "—",
            activated: !!inv.used_at,
            activated_at: inv.used_at || null,
            last_login: null,
            access_code: null,
          }));
          setUsers(mapped);
        } catch (e) {
          console.error("[PortalAccountsTab reload]", e);
        } finally {
          setLoading(false);
        }
      })();
    }
    showToast("Patient invited successfully");
  };

  if (loading) return (
    <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 14 }}>
      Loading portal accounts…
    </div>
  );

  return (
    <div>
      {toastMsg && createPortal(
        <div style={{
          position: "fixed", top: 16, right: 16, zIndex: 2000,
          background: "#052e16", border: "1px solid #14532d",
          color: "#4ade80", borderRadius: 8, padding: "10px 16px",
          fontSize: 13, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }}>
          ✓ {toastMsg}
        </div>,
        document.body
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 13, color: "#64748b" }}>
            {users.filter(u => u.activated).length} activated · {users.filter(u => !u.activated).length} pending
          </div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowInvite(true)}>
          + Invite Patient
        </button>
      </div>

      {users.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 24px", color: "#64748b", fontSize: 14, border: "1px solid var(--border)", borderRadius: 8 }}>
          No portal invites sent yet. Use "Invite Patient" to get started.
        </div>
      ) : (
        <TableWrapper>
          <THead cols={["Patient", "Email", "Status", isDemo ? "Access Code" : "Auth Method", "Last Login", "Actions"]} />
          <tbody>
            {users.map(user => (
              <TRow key={user.id}>
                <TD>
                  <div style={{ fontWeight: 600, color: "#f1f5f9" }}>{user.first_name} {user.last_name}</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>DOB: {user.date_of_birth}</div>
                </TD>
                <TD>{user.email}</TD>
                <TD>
                  <StatusBadge status={user.activated ? "activated" : "not_activated"} />
                </TD>
                <TD>
                  {isDemo ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{
                        fontFamily: "monospace", fontSize: 14, letterSpacing: 2,
                        color: revealedCodes[user.id] ? "#4ade80" : "#475569",
                      }}>
                        {revealedCodes[user.id] ? user.access_code : "••••••"}
                      </span>
                      <ActionBtn onClick={() => toggleCode(user.id)}>
                        {revealedCodes[user.id] ? "Hide" : "Show"}
                      </ActionBtn>
                    </div>
                  ) : (
                    <span style={{ fontSize: 12, color: "#4ade80" }}>Magic Link</span>
                  )}
                </TD>
                <TD style={{ fontSize: 12, color: "#64748b" }}>
                  {user.last_login ? formatDateTime(user.last_login) : "—"}
                </TD>
                <TD>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {isDemo && (
                      <ActionBtn
                        onClick={() => toggleActivation(user.id)}
                        variant={user.activated ? "danger" : "success"}
                      >
                        {user.activated ? "Deactivate" : "Activate"}
                      </ActionBtn>
                    )}
                    <ActionBtn
                      onClick={() => copyInviteLink(user)}
                      variant={copiedId === user.id ? "success" : "default"}
                    >
                      {copiedId === user.id ? "✓ Noted" : (isDemo ? "Copy Link" : "Resend Invite")}
                    </ActionBtn>
                  </div>
                </TD>
              </TRow>
            ))}
          </tbody>
        </TableWrapper>
      )}

      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onInvite={(u) => { handleInvite(u); setShowInvite(false); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2: Intake Forms
// ---------------------------------------------------------------------------

function FormDataView({ formData }) {
  if (!formData || Object.keys(formData).length === 0) {
    return <div style={{ color: "#64748b", fontSize: 13 }}>No form data submitted.</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {Object.entries(formData).map(([key, val]) => (
        <div key={key} style={{ display: "flex", gap: 12 }}>
          <span style={{
            fontSize: 12, fontWeight: 700, color: "#64748b",
            textTransform: "uppercase", letterSpacing: 0.4,
            minWidth: 140, flexShrink: 0,
          }}>
            {key.replace(/_/g, " ")}
          </span>
          <span style={{ fontSize: 13, color: "#cbd5e1" }}>
            {Array.isArray(val) ? val.join(", ") : String(val)}
          </span>
        </div>
      ))}
    </div>
  );
}

function IntakeFormsTab() {
  const [forms, setForms] = useState(isDemo ? INITIAL_PENDING_FORMS : []);
  const [loading, setLoading] = useState(!isDemo);
  const [reviewingForm, setReviewingForm] = useState(null);
  const [importedIds, setImportedIds] = useState(new Set());

  useEffect(() => {
    if (isDemo) return;
    let active = true;
    (async () => {
      try {
        // Query 1 — forms without join (no FK to patients.id uuid)
        const { data: formData, error } = await db
          .from("intake_forms")
          .select("id, patient_id, form_type, status, form_data, submitted_at, created_at")
          .order("submitted_at", { ascending: false })
          .limit(100);
        if (error) { console.error("[IntakeFormsTab]", error); }

        const formRows = formData || [];

        // Query 2 — patient names for the collected patient_ids
        const patientIds = [...new Set(formRows.map(f => f.patient_id).filter(Boolean))];
        let patientMap = {};
        if (patientIds.length > 0) {
          const { data: patientData } = await db
            .from("patients")
            .select("id, first_name, last_name")
            .in("id", patientIds);
          patientMap = Object.fromEntries((patientData || []).map(p => [p.id, p]));
        }

        if (active) {
          const mapped = formRows.map(f => ({
            id: f.id,
            patient_id: f.patient_id || "",
            patient_name: patientMap[f.patient_id]
              ? `${patientMap[f.patient_id].first_name || ""} ${patientMap[f.patient_id].last_name || ""}`.trim()
              : "Unknown Patient",
            form_type: f.form_type || "intake",
            status: f.status || "pending",
            submitted_at: f.submitted_at || f.created_at || null,
            form_data: f.form_data || {},
          }));
          setForms(mapped);
        }
      } catch (e) {
        console.error("[IntakeFormsTab]", e);
        // Graceful empty state — table may not exist yet
        if (active) setForms([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleImport = (formId) => {
    setImportedIds(prev => new Set([...prev, formId]));
  };

  if (loading) return (
    <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 14 }}>
      Loading intake forms…
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          {forms.filter(f => f.status === "submitted").length} awaiting review
        </div>
      </div>

      {forms.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 24px", color: "#64748b", fontSize: 14, border: "1px solid var(--border)", borderRadius: 8 }}>
          No intake forms submitted yet.
        </div>
      ) : (
      <TableWrapper>
        <THead cols={["Patient", "Form Type", "Status", "Submitted", "Actions"]} />
        <tbody>
          {forms.map(form => (
            <TRow key={form.id}>
              <TD>
                <div style={{ fontWeight: 600, color: "#f1f5f9" }}>{form.patient_name}</div>
                <div style={{ fontSize: 11, color: "#64748b" }}>ID: {form.patient_id}</div>
              </TD>
              <TD>{formatFormType(form.form_type)}</TD>
              <TD><StatusBadge status={form.status} /></TD>
              <TD style={{ fontSize: 12, color: "#64748b" }}>
                {form.submitted_at ? formatDateTime(form.submitted_at) : "—"}
              </TD>
              <TD>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {form.status !== "pending" && (
                    <ActionBtn onClick={() => setReviewingForm(form)}>
                      Review
                    </ActionBtn>
                  )}
                  {form.status === "submitted" && form.form_type === "intake" && (
                    importedIds.has(form.id) ? (
                      <ActionBtn variant="success" disabled>✓ Imported</ActionBtn>
                    ) : (
                      <ActionBtn variant="primary" onClick={() => handleImport(form.id)}>
                        Import to Chart
                      </ActionBtn>
                    )
                  )}
                </div>
              </TD>
            </TRow>
          ))}
        </tbody>
      </TableWrapper>
      )}

      {reviewingForm && (
        <Modal title={`Review: ${formatFormType(reviewingForm.form_type)} — ${reviewingForm.patient_name}`} onClose={() => setReviewingForm(null)} width={560}>
          <div style={{
            background: "var(--navy)", border: "1px solid var(--border)",
            borderRadius: 8, padding: 16, marginBottom: 16,
          }}>
            <div style={{ display: "flex", gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 11, color: "var(--text-2)", marginBottom: 2 }}>PATIENT</div>
                <div style={{ color: "var(--text-1)", fontWeight: 600 }}>{reviewingForm.patient_name}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--text-2)", marginBottom: 2 }}>STATUS</div>
                <StatusBadge status={reviewingForm.status} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "var(--text-2)", marginBottom: 2 }}>SUBMITTED</div>
                <div style={{ color: "var(--text-2)", fontSize: 13 }}>
                  {reviewingForm.submitted_at ? formatDateTime(reviewingForm.submitted_at) : "—"}
                </div>
              </div>
            </div>
            <div style={{ height: 1, background: "var(--border)", margin: "12px 0" }} />
            <FormDataView formData={reviewingForm.form_data} />
          </div>
          <button className="btn btn-primary" onClick={() => setReviewingForm(null)} style={{ width: "100%" }}>
            Close
          </button>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 3: Portal Messages
// ---------------------------------------------------------------------------

function ReplyModal({ message, onClose, onReplied }) {
  const [replyBody, setReplyBody] = useState("");
  const [sent, setSent] = useState(false);

  const handleSend = () => {
    onReplied(message.id);
    setSent(true);
  };

  if (sent) {
    return (
      <Modal title="Reply Sent" onClose={onClose}>
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <p style={{ color: "var(--text-2)", fontSize: 14 }}>
            Your reply to <strong style={{ color: "var(--text-1)" }}>{message.patient_name}</strong> has been sent.
          </p>
          <button className="btn btn-primary" onClick={onClose} style={{ width: "100%", marginTop: 16 }}>Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Reply to ${message.patient_name}`} onClose={onClose}>
      <div style={{
        background: "var(--navy)", border: "1px solid var(--border)", borderRadius: 8,
        padding: "12px 14px", marginBottom: 16,
      }}>
        <div style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 4 }}>RE: {message.subject}</div>
        <div style={{ fontSize: 13, color: "var(--text-2)", fontStyle: "italic" }}>
          "{message.body.substring(0, 120)}{message.body.length > 120 ? "..." : ""}"
        </div>
      </div>

      <FormField label="Reply">
        <ProviderTextarea
          value={replyBody}
          onChange={e => setReplyBody(e.target.value)}
          placeholder="Type your reply to the patient..."
          rows={5}
        />
      </FormField>

      <button
        className="btn btn-primary"
        onClick={handleSend}
        disabled={!replyBody.trim()}
        style={{ width: "100%" }}
      >
        Send Reply
      </button>
    </Modal>
  );
}

function PortalMessagesTab() {
  const appCtx = useApp();
  const practiceId = appCtx?.practiceId;
  const [msgs, setMsgs] = useState(isDemo ? INITIAL_PORTAL_MSGS : []);
  const [loading, setLoading] = useState(!isDemo);
  const [replyingTo, setReplyingTo] = useState(null);

  useEffect(() => {
    if (isDemo) return;
    let active = true;
    (async () => {
      try {
        // Query 1 — messages without join (patient_id is text; no FK to patients.id uuid)
        // Group C (2026-06-13): portal_messages flipped to Cloud SQL.
        let q = db
          .from("portal_messages")
          .select("id, patient_id, subject, body, message_type, sender_type, is_read, flagged_urgent, created_at, practice_id")
          .eq("sender_type", "patient")
          .order("created_at", { ascending: false })
          .limit(50);
        if (practiceId) q = q.eq("practice_id", practiceId);
        const { data: msgData, error } = await q;
        if (error) { console.error("[PortalMessagesTab]", error); }

        const messages = msgData || [];

        // Query 2 — patient names for the collected patient_ids
        const patientIds = [...new Set(messages.map(m => m.patient_id).filter(Boolean))];
        let patientMap = {};
        if (patientIds.length > 0) {
          const { data: patientData } = await db
            .from("patients")
            .select("id, first_name, last_name")
            .in("id", patientIds);
          patientMap = Object.fromEntries((patientData || []).map(p => [p.id, p]));
        }

        if (active) {
          const mapped = messages.map(m => ({
            id: m.id,
            patient_id: m.patient_id || "",
            patient_name: patientMap[m.patient_id]
              ? `${patientMap[m.patient_id].first_name || ""} ${patientMap[m.patient_id].last_name || ""}`.trim()
              : "Portal Patient",
            subject: m.subject || "(No subject)",
            body: m.body || "",
            created_at: m.created_at,
            is_read: !!m.is_read,
            is_urgent: !!m.flagged_urgent,
          }));
          setMsgs(mapped);
        }
      } catch (e) {
        console.error("[PortalMessagesTab]", e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [practiceId]);

  const unreadCount = msgs.filter(m => !m.is_read).length;

  const handleReplied = (msgId) => {
    setMsgs(msgs.map(m => m.id === msgId ? { ...m, is_read: true } : m));
    setReplyingTo(null);
    // Production: persist read state to Supabase
    if (!isDemo && msgId && /^[0-9a-f-]{36}$/.test(msgId)) {
      db.from("portal_messages").update({ is_read: true }).eq("id", msgId)
        .then(({ error }) => { if (error) console.error("[PortalMessagesTab mark read]", error); });
    }
  };

  const handleMarkRead = (msgId) => {
    setMsgs(msgs.map(m => m.id === msgId ? { ...m, is_read: true } : m));
    // Production: persist read state to Supabase
    if (!isDemo && msgId && /^[0-9a-f-]{36}$/.test(msgId)) {
      db.from("portal_messages").update({ is_read: true }).eq("id", msgId)
        .then(({ error }) => { if (error) console.error("[PortalMessagesTab mark read]", error); });
    }
  };

  if (loading) return (
    <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 14 }}>
      Loading messages…
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          {unreadCount} unread · {msgs.filter(m => m.is_urgent).length} urgent
        </div>
      </div>

      {msgs.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 24px", color: "#64748b", fontSize: 14, border: "1px solid var(--border)", borderRadius: 8 }}>
          No patient messages yet.
        </div>
      ) : (
      <TableWrapper>
        <THead cols={["Patient", "Subject", "Date", "Priority", "Status", "Actions"]} />
        <tbody>
          {msgs.map(msg => (
            <TRow key={msg.id} highlight={msg.is_urgent}>
              <TD>
                <div style={{ fontWeight: 600, color: "#f1f5f9" }}>{msg.patient_name}</div>
                <div style={{ fontSize: 11, color: "#64748b" }}>ID: {msg.patient_id}</div>
              </TD>
              <TD>
                <div style={{ color: msg.is_read ? "#94a3b8" : "#f1f5f9", fontWeight: msg.is_read ? 400 : 700 }}>
                  {!msg.is_read && (
                    <span style={{
                      display: "inline-block", width: 7, height: 7,
                      borderRadius: "50%", background: "#3b82f6",
                      marginRight: 7, verticalAlign: "middle",
                    }} />
                  )}
                  {msg.subject}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  {msg.body.substring(0, 55)}{msg.body.length > 55 ? "..." : ""}
                </div>
              </TD>
              <TD style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
                {formatDateTime(msg.created_at)}
              </TD>
              <TD>
                {msg.is_urgent ? (
                  <span style={{
                    background: "#450a0a", color: "#f87171", border: "1px solid #7f1d1d",
                    borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700,
                  }}>
                    🔴 Urgent
                  </span>
                ) : (
                  <span style={{ color: "#64748b", fontSize: 12 }}>Normal</span>
                )}
              </TD>
              <TD>
                <StatusBadge status={msg.is_read ? "read" : "unread"} />
              </TD>
              <TD>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <ActionBtn variant="primary" onClick={() => setReplyingTo(msg)}>
                    Reply
                  </ActionBtn>
                  {!msg.is_read && (
                    <ActionBtn onClick={() => handleMarkRead(msg.id)}>
                      Mark Read
                    </ActionBtn>
                  )}
                </div>
              </TD>
            </TRow>
          ))}
        </tbody>
      </TableWrapper>
      )}

      {replyingTo && (
        <ReplyModal
          message={replyingTo}
          onClose={() => setReplyingTo(null)}
          onReplied={handleReplied}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 4: Portal Bills
// ---------------------------------------------------------------------------

function AddBillModal({ onClose, onAdd }) {
  const [portalPatients, setPortalPatients] = useState(isDemo ? MOCK_PORTAL_USERS : []);
  const [patientId, setPatientId] = useState(isDemo && MOCK_PORTAL_USERS.length > 0 ? MOCK_PORTAL_USERS[0].id : "");
  const [description, setDescription] = useState("");
  const [dos, setDos] = useState("");
  const [amount, setAmount] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (isDemo) return;
    db
      .from("patients")
      .select("id, first_name, last_name")
      .eq("portal_access_enabled", true)
      .order("last_name", { ascending: true })
      .limit(200)
      .then(({ data, error }) => {
        if (error) { console.error("[AddBillModal patients]", error); return; }
        const pts = data || [];
        setPortalPatients(pts.map(p => ({ id: p.id, patient_id: p.id, first_name: p.first_name || "", last_name: p.last_name || "" })));
        if (pts.length > 0) setPatientId(pts[0].id);
      });
  }, []);

  const handleAdd = () => {
    const patient = portalPatients.find(u => u.id === patientId);
    if (!patient) return;
    const newBill = {
      id: "PB" + Date.now(),
      patient_name: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
      patient_id: patient.patient_id || patient.id,
      description,
      dos,
      amount_due: parseFloat(amount),
      amount_paid: 0,
      status: "unpaid",
      due_date: dueDate,
    };
    onAdd(newBill);
    setAdded(true);
  };

  if (added) {
    return (
      <Modal title="Bill Added" onClose={onClose}>
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
          <p style={{ color: "#94a3b8", fontSize: 14 }}>Bill has been added to the patient portal.</p>
          <button className="btn btn-primary" onClick={onClose} style={{ width: "100%", marginTop: 16 }}>Done</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Add Bill to Portal" onClose={onClose}>
      <FormField label="Patient">
        <select
          value={patientId}
          onChange={e => setPatientId(e.target.value)}
          style={{
            width: "100%", boxSizing: "border-box",
            background: "var(--navy)", border: "1px solid var(--border-2)", borderRadius: 8,
            padding: "9px 12px", color: "var(--text-1)", fontSize: 14,
            outline: "none", fontFamily: "inherit",
          }}
        >
          {portalPatients.length === 0 && (
            <option value="">Loading patients…</option>
          )}
          {portalPatients.map(u => (
            <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
          ))}
        </select>
      </FormField>
      <FormField label="Description">
        <ProviderInput value={description} onChange={e => setDescription(e.target.value)} placeholder="e.g. Office Visit Copay" />
      </FormField>
      <FormField label="Date of Service">
        <ProviderInput type="date" value={dos} onChange={e => setDos(e.target.value)} />
      </FormField>
      <FormField label="Amount Due ($)">
        <ProviderInput type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
      </FormField>
      <FormField label="Due Date">
        <ProviderInput type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
      </FormField>
      <button
        className="btn btn-primary"
        onClick={handleAdd}
        disabled={!description || !dos || !amount || !dueDate}
        style={{ width: "100%", marginTop: 8 }}
      >
        Add Bill
      </button>
    </Modal>
  );
}

function PortalBillsTab() {
  const appCtx = useApp();
  const practiceId = appCtx?.practiceId;
  const [bills, setBills] = useState(isDemo ? INITIAL_PORTAL_BILLS : []);
  const [loading, setLoading] = useState(!isDemo);
  const [showAddBill, setShowAddBill] = useState(false);

  useEffect(() => {
    if (isDemo) return;
    let active = true;
    (async () => {
      try {
        // Query 1 — bills without join. Group C (2026-06-13): portal_bills
        // flipped to Cloud SQL + GAINED a practice_id column (the multi-tenancy
        // SECURITY FIX — it had none, RLS alone gated access). db-query
        // force-ANDs practice_id from the JWT; the explicit .eq keeps the spec
        // honest + demo/Supabase-path correct.
        let billQ = db
          .from("portal_bills")
          .select("id, patient_id, description, dos, amount_due, amount_paid, status, due_date, created_at, practice_id")
          .order("due_date", { ascending: false })
          .limit(200);
        if (practiceId) billQ = billQ.eq("practice_id", practiceId);
        const { data: billData, error } = await billQ;
        if (error) { console.error("[PortalBillsTab]", error); }

        const billRows = billData || [];

        // Query 2 — patient names for the collected patient_ids
        const patientIds = [...new Set(billRows.map(b => b.patient_id).filter(Boolean))];
        let patientMap = {};
        if (patientIds.length > 0) {
          const { data: patientData } = await db
            .from("patients")
            .select("id, first_name, last_name")
            .in("id", patientIds);
          patientMap = Object.fromEntries((patientData || []).map(p => [p.id, p]));
        }

        if (active) {
          const mapped = billRows.map(b => ({
            id: b.id,
            patient_id: b.patient_id || "",
            patient_name: patientMap[b.patient_id]
              ? `${patientMap[b.patient_id].first_name || ""} ${patientMap[b.patient_id].last_name || ""}`.trim()
              : "Portal Patient",
            description: b.description || "",
            dos: b.dos || "",
            amount_due: parseFloat(b.amount_due) || 0,
            amount_paid: parseFloat(b.amount_paid) || 0,
            status: b.status || "unpaid",
            due_date: b.due_date || "",
          }));
          setBills(mapped);
        }
      } catch (e) {
        console.error("[PortalBillsTab]", e);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [practiceId]);

  const totalOutstanding = bills.filter(b => b.status === "unpaid").reduce((s, b) => s + (b.amount_due || 0), 0);

  const handleMarkPaid = (billId) => {
    setBills(bills.map(b => b.id === billId
      ? { ...b, status: "paid", amount_paid: b.amount_due }
      : b
    ));
    // Production: persist to Supabase
    if (!isDemo && billId && /^[0-9a-f-]{36}$/.test(billId)) {
      const bill = bills.find(b => b.id === billId);
      if (bill) {
        db.from("portal_bills")
          .update({ status: "paid", amount_paid: bill.amount_due })
          .eq("id", billId)
          .then(({ error }) => { if (error) console.error("[PortalBillsTab mark paid]", error); });
      }
    }
  };

  const handleAddBill = (bill) => {
    setBills(prev => [...prev, bill]);
    setShowAddBill(false);
  };

  if (loading) return (
    <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 14 }}>
      Loading portal bills…
    </div>
  );

  return (
    <div>
      {/* Summary stat */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{
          background: "#451a03", border: "1px solid #78350f",
          borderRadius: 10, padding: "14px 20px",
          display: "flex", alignItems: "center", gap: 16,
        }}>
          <div>
            <div style={{ fontSize: 12, color: "#fbbf24", fontWeight: 600, marginBottom: 2 }}>TOTAL OUTSTANDING</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#fbbf24" }}>${totalOutstanding.toFixed(2)}</div>
          </div>
          <div style={{ fontSize: 28 }}>💳</div>
        </div>
        <div style={{
          background: "#052e16", border: "1px solid #14532d",
          borderRadius: 10, padding: "14px 20px",
        }}>
          <div style={{ fontSize: 12, color: "#4ade80", fontWeight: 600, marginBottom: 2 }}>PATIENTS WITH BALANCE</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "#4ade80" }}>
            {new Set(bills.filter(b => b.status === "unpaid").map(b => b.patient_id)).size}
          </div>
        </div>

        <div style={{ marginLeft: "auto" }}>
          <button className="btn btn-primary" onClick={() => setShowAddBill(true)}>
            + Add Bill
          </button>
        </div>
      </div>

      <TableWrapper>
        <THead cols={["Patient", "Description", "DOS", "Amount Due", "Amount Paid", "Status", "Due Date", "Actions"]} />
        <tbody>
          {bills.map(bill => (
            <TRow key={bill.id}>
              <TD>
                <div style={{ fontWeight: 600, color: "#f1f5f9" }}>{bill.patient_name}</div>
                <div style={{ fontSize: 11, color: "#64748b" }}>ID: {bill.patient_id}</div>
              </TD>
              <TD>
                <div style={{ maxWidth: 200, color: "#cbd5e1", fontSize: 13 }}>{bill.description}</div>
              </TD>
              <TD style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
                {formatDate(bill.dos)}
              </TD>
              <TD>
                <span style={{ fontWeight: 700, color: bill.status === "unpaid" ? "#fbbf24" : "#64748b" }}>
                  ${bill.amount_due.toFixed(2)}
                </span>
              </TD>
              <TD>
                <span style={{ color: bill.amount_paid > 0 ? "#4ade80" : "#64748b" }}>
                  ${bill.amount_paid.toFixed(2)}
                </span>
              </TD>
              <TD>
                <StatusBadge status={bill.status} />
              </TD>
              <TD style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
                {formatDate(bill.due_date)}
              </TD>
              <TD>
                {bill.status === "unpaid" ? (
                  <ActionBtn variant="success" onClick={() => handleMarkPaid(bill.id)}>
                    Mark Paid
                  </ActionBtn>
                ) : (
                  <ActionBtn disabled>Paid ✓</ActionBtn>
                )}
              </TD>
            </TRow>
          ))}
        </tbody>
      </TableWrapper>

      {showAddBill && (
        <AddBillModal
          onClose={() => setShowAddBill(false)}
          onAdd={handleAddBill}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PortalManagement (default export)
// ---------------------------------------------------------------------------

export default function PortalManagement() {
  const appCtx = useApp();
  const practiceId = appCtx?.practiceId;
  const [tab, setTab] = useState("accounts");

  // Badge counts — demo uses mock constants; production queries Supabase
  const [unreadMsgCount, setUnreadMsgCount] = useState(
    isDemo ? INITIAL_PORTAL_MSGS.filter(m => !m.is_read).length : 0
  );
  const [pendingFormCount, setPendingFormCount] = useState(
    isDemo ? INITIAL_PENDING_FORMS.filter(f => f.status === "submitted").length : 0
  );

  useEffect(() => {
    if (isDemo) return;
    // Group C (2026-06-13): portal_messages + intake_forms flipped to Cloud SQL.
    let msgQ = db
      .from("portal_messages")
      .select("id", { count: "exact", head: true })
      .eq("is_read", false)
      .eq("sender_type", "patient");
    if (practiceId) msgQ = msgQ.eq("practice_id", practiceId);
    msgQ.then(({ count }) => setUnreadMsgCount(count || 0))
      .catch(() => {});

    let formQ = db.from("intake_forms")
      .select("id", { count: "exact", head: true })
      .eq("status", "submitted");
    if (practiceId) formQ = formQ.eq("practice_id", practiceId);
    formQ.then(({ count }) => setPendingFormCount(count || 0))
      .catch(() => {});
  }, [practiceId]);

  const tabs = [
    { key: "accounts", label: "Portal Accounts" },
    { key: "forms", label: "Intake Forms", badge: pendingFormCount },
    { key: "messages", label: "Portal Messages", badge: unreadMsgCount },
    { key: "bills", label: "Portal Bills" },
  ];

  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      {/* Header */}
      <div style={{
        padding: "20px 24px 0",
        borderBottom: "1px solid var(--border)",
        background: "var(--navy)",
      }}>
        <div style={{ marginBottom: 16 }}>
          <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: "var(--text-1)" }}>
            Patient Portal Management
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: "var(--text-2)" }}>
            Manage portal access, review intake forms, respond to messages, and track patient billing.
          </p>
        </div>
        <TabBar tabs={tabs} active={tab} onChange={setTab} />
      </div>

      {/* Tab content */}
      <div style={{ padding: "20px 24px" }}>
        {tab === "accounts" && <PortalAccountsTab />}
        {tab === "forms" && <IntakeFormsTab />}
        {tab === "messages" && <PortalMessagesTab />}
        {tab === "bills" && <PortalBillsTab />}
      </div>
    </div>
  );
}
